import type { LinkDeviceResult } from "@/data/queries"
import type { BackendErrorCode } from "@/model/BackendErrors"
import { BLOOD_GLUCOSE_DEVICE_NAME } from "@/util/constants"
import type { CameraStatus } from "@/util/hooks/useCameraStream"
import { upperFirst } from "lodash"

// Titles and descriptions of the dialogs shown while scanning a device barcode

const BLOOD_PRESSURE_DEVICE_NAME = "blood pressure monitor"

const IMEI_ENDING_LENGTH = 4

const imeiEnding = (imei: string): string => imei.slice(-IMEI_ENDING_LENGTH)

export type ScanDeviceMessage = {
  title: string
  description: string
}

export const deviceLinkedMessageFor = (
  outcome: LinkDeviceResult,
  imei: string,
): ScanDeviceMessage => {
  switch (outcome) {
    case "linked":
      return {
        title: "Device linked!",
        description: `${upperFirst(BLOOD_GLUCOSE_DEVICE_NAME)} device linked — IMEI ending in ${imeiEnding(imei)}.`,
      }
    case "device-already-added":
      return {
        title: "Device already linked",
        description: `This ${BLOOD_GLUCOSE_DEVICE_NAME} (IMEI ending in ${imeiEnding(imei)}) is already linked to your account.`,
      }
  }
}

export type DeviceLinkResolution = "scan-again" | "check-address" | "contact-support"

export type DeviceLinkErrorMessage = ScanDeviceMessage & {
  resolution: DeviceLinkResolution
}

const unknownDeviceLinkError: DeviceLinkErrorMessage = {
  title: "Something went wrong",
  description: "We couldn't link your device right now. Scan again, or contact Support.",
  resolution: "scan-again",
}

const deviceLinkErrors: Partial<Record<BackendErrorCode, DeviceLinkErrorMessage>> = {
  ADDRESS_VALIDATION_FAILED: {
    title: "We couldn't validate your address",
    description:
      "The address you entered could not be validated. Please check your shipping address and try again.",
    resolution: "check-address",
  },
  NO_ACTIVE_DIABETES_PRESCRIPTION: {
    title: "No active diabetes prescription",
    description: `We can't link a ${BLOOD_GLUCOSE_DEVICE_NAME} without an active diabetes prescription.`,
    resolution: "contact-support",
  },
  NO_ACTIVE_HYPERTENSION_PRESCRIPTION: {
    title: "This device is not part of your prescription",
    description: `We can't link a ${BLOOD_PRESSURE_DEVICE_NAME} without an active hypertension prescription.`,
    resolution: "contact-support",
  },
  DEVICE_ASSIGNED_TO_OTHER_PATIENT: {
    title: "This device is already linked",
    description:
      "This device is already linked to another patient account. Contact Bloomlife Support.",
    resolution: "contact-support",
  },
  DEVICE_NOT_FOUND: {
    title: "We couldn't recognize this device",
    description: "This barcode doesn't match a SmartMeter device. Contact Support.",
    resolution: "scan-again",
  },
  UNKNOWN_ERROR: unknownDeviceLinkError,
}

export const deviceLinkErrorMessageFor = (errorCode: BackendErrorCode): DeviceLinkErrorMessage =>
  deviceLinkErrors[errorCode] ?? unknownDeviceLinkError

export const supportContactedMessage: ScanDeviceMessage = {
  title: "We've contacted Support",
  description:
    "Bloomlife Support has been notified and will reach out within 1 business day. You'll get a text or email in the meantime — no need to do anything else.",
}

export const scannerUnavailableMessage: ScanDeviceMessage = {
  title: "Scanning not available",
  description:
    "The barcode scanner could not start on this device. Check your connection and try again, or contact Support.",
}

export const cameraMessageFor = (status: CameraStatus): ScanDeviceMessage =>
  status === "unavailable"
    ? {
        title: "Camera not available",
        description:
          "We couldn't find a camera on this device. Try again on a phone or tablet with a camera.",
      }
    : {
        title: "Camera access needed",
        description:
          "We use your camera to scan the barcode on your device box. Allow camera access in your browser or phone settings.",
      }
